import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.js';
import { FiSearch, FiVideo, FiMessageSquare, FiBell, FiSettings, FiLogOut, FiChevronDown } from 'react-icons/fi';

function NavBar() {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  const navLinks = [
    { text: 'Following', path: '/following' },
    { text: 'Browse', path: '/browse' },
    { text: 'Teams', path: '/teams' },
    { text: 'Tournaments', path: '/tournaments' }
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-[#18181b] border-b border-gray-800 h-12">
      <div className="flex items-center justify-between h-full px-4">
        {/* Left Section: Logo */}
        <div className="flex items-center h-full">
          <Link to="/" className="text-harmony-orange font-bold text-xl mr-4">
            StreamerWars
          </Link>
          
          <div className="h-full flex items-center">
            {navLinks.map((link) => (
              <Link 
                key={link.text} 
                to={link.path}
                className={`font-medium h-full px-3 flex items-center transition-colors duration-200 mx-1
                  ${location.pathname === link.path 
                    ? 'text-white border-b-2 border-harmony-orange' 
                    : 'text-gray-100 hover:text-white hover:bg-[#2a2a2d]'}`}
              >
                {link.text}
              </Link>
            ))}
          </div>
        </div>
        
        {/* Center: Search Bar */}
        <div className="hidden md:flex items-center max-w-md w-full relative mx-4">
          <div className="relative w-full">
            <input
              type="text"
              placeholder="Search"
              className="w-full pl-10 pr-4 py-1 bg-[#2a2a2d] text-white placeholder-gray-400 rounded-md border border-transparent focus:border-gray-700 focus:outline-none"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <div className="absolute left-0 top-0 h-full flex items-center pl-3">
              <FiSearch className="h-4 w-4 text-gray-400" />
            </div>
          </div>
        </div>
        
        {/* Right Section: User Controls */}
        <div className="flex items-center">
          {currentUser ? (
            <>
              {/* Go Live Button */}
              <Link 
                to="/stream-manager" 
                className="mr-3 bg-harmony-orange hover:bg-harmony-orange/90 text-white font-medium text-sm px-3 py-1 rounded flex items-center"
              >
                <FiVideo className="mr-1 h-4 w-4" />
                GO LIVE
              </Link>
              
              {/* Icons */}
              <button className="mx-2 relative text-gray-100 hover:text-white">
                <FiBell className="h-5 w-5" />
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                  3
                </span>
              </button>
              
              <button className="mx-2 relative text-gray-100 hover:text-white">
                <FiMessageSquare className="h-5 w-5" />
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                  2
                </span>
              </button>
              
              {/* User Menu */}
              <div className="ml-2 relative">
                <button
                  type="button"
                  className="flex items-center text-sm rounded focus:outline-none"
                  onClick={() => setIsOpen(!isOpen)}
                >
                  <img
                    className="h-8 w-8 rounded-full"
                    src="/assets/default_avatar.png"
                    alt="User profile"
                  />
                  <FiChevronDown className={`ml-1 h-4 w-4 text-gray-300 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isOpen && (
                  <div className="origin-top-right absolute right-0 mt-2 w-48 rounded bg-[#1f1f23] shadow-lg py-1 border border-gray-800 z-50">
                    <Link
                      to="/profile"
                      className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white"
                      onClick={() => setIsOpen(false)}
                    >
                      Channel
                    </Link>
                    <Link
                      to="/settings"
                      className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white"
                      onClick={() => setIsOpen(false)}
                    >
                      <FiSettings className="inline mr-2 h-4 w-4" />
                      Settings
                    </Link>
                    <Link
                      to="/stream-manager"
                      className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white"
                      onClick={() => setIsOpen(false)}
                    >
                      <FiVideo className="inline mr-2 h-4 w-4" />
                      Stream Manager
                    </Link>
                    <div className="border-t border-gray-800 my-1"></div>
                    <button
                      className="block w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white"
                      onClick={handleLogout}
                    >
                      <FiLogOut className="inline mr-2 h-4 w-4" />
                      Log Out
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <Link
                to="/login"
                className="text-white hover:bg-gray-700 px-3 py-1.5 rounded text-sm font-medium mr-2"
              >
                Log In
              </Link>
              <Link
                to="/register"
                className="bg-harmony-orange hover:bg-harmony-orange/90 text-white px-3 py-1.5 rounded text-sm font-medium"
              >
                Sign Up
              </Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}

export default NavBar; 